/**
 * Companies Discovery Page (preview).
 *
 * Three discovery modes share the left side; right rail is a persistent
 * selection panel (Considering / Saved / Disqualified).
 *
 *   Search    → name lookup with type-ahead filtering
 *   Headlines → editorial picks, single-line headline + 4 cards per row
 *   Find Like → pick up to 4 seed companies, get similar matches with reasons
 *
 * Selection state flows through the SelectionsProvider (hooks/useSelections),
 * so cycle/disqualify/save persist across sessions via /api/selections.
 *
 * Canonical Companies surface. Mounted at /explore/companies; the legacy
 * alphabetical-grid hub was retired in the swap. /explore/companies/discovery
 * redirects here for back-compat with in-flight links.
 *
 * Data: companies + clusters are hand-coded mocks per the brief's
 * acceptance of placeholder data (the real per-company fields like stage,
 * mode, fitRoles, compPercentile, distinctive aren't enriched yet). Real
 * wiring lands when enrichment ships.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExploreIcon from '@mui/icons-material/Explore';
import TuneIcon from '@mui/icons-material/Tune';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { SAGE, CREAM, WARM } from '../theme';
import { SelectionsProvider, useSelections } from '../hooks/useSelections';
import EntityDetailModal from '../components/entity/EntityDetailModal';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

// ===========================================================================
// MOCK DATA — replace with backend feeds when enrichment lands.
// ===========================================================================

// `id` is the company's domain — used as both the selection-state key and
// the navigation target for /explore/companies/:domain. One canonical key
// keeps everything aligned.
const COMPANIES = [
  { id: 'stripe.com',         name: 'Stripe',     logo: 'S',  color: '#635bff', tagline: 'Financial infrastructure for the internet', stage: 'Late-stage', size: 8000, hq: 'San Francisco, CA', funding: 'Private · last $91.5B', founded: 2010, openRoles: 142, fitRoles: 26, compPercentile: 70, mode: 'Hybrid', seniorityHeavy: 'Senior–Staff', distinctive: ['Sabbatical (5yr+)', 'Backup childcare'], topValues: ['Move with urgency', 'Think rigorously', 'Be meticulous in your craft'], synopsis: 'Stripe builds payment APIs and financial tools used by millions of businesses. Heavy emphasis on engineering quality, technical writing, and ownership culture. Recently expanded into AI-powered fraud and embedded finance — driving aggressive hiring in ML, data, and infra.' },
  { id: 'notion.so',          name: 'Notion',     logo: 'N',  color: '#000000', tagline: 'All-in-one workspace', stage: 'Late-stage', size: 600, hq: 'San Francisco, CA', funding: 'Private · last $10B', founded: 2013, openRoles: 38, fitRoles: 9, compPercentile: 65, mode: 'Hybrid', seniorityHeavy: 'Senior', distinctive: ['Quarterly off-sites', 'Sabbatical fund'], topValues: ['Craft', 'Long-term thinking', 'User obsession'], synopsis: 'Notion makes a flexible workspace tool used by individuals and teams. Known for craft-heavy product thinking, small focused teams, and a strong design culture. Hiring is steady but deliberate.' },
  { id: 'figma.com',          name: 'Figma',      logo: 'F',  color: '#a259ff', tagline: 'Collaborative design tool', stage: 'Public', size: 1500, hq: 'San Francisco, CA', funding: 'Public', founded: 2012, openRoles: 54, fitRoles: 11, compPercentile: 72, mode: 'Remote-friendly', seniorityHeavy: 'Senior', distinctive: ['Remote-first weeks', 'Internal mentorship'], topValues: ['Play', 'Grow as you go', 'Strong opinions, weakly held'], synopsis: 'Figma builds the leading collaborative design platform. Strong cross-functional collaboration culture between design, product, and engineering. Hiring focused on platform engineering and AI-powered design tools.' },
  { id: 'duolingo.com',       name: 'Duolingo',   logo: 'D',  color: '#58cc02', tagline: 'Language learning, gamified', stage: 'Public', size: 800, hq: 'Pittsburgh, PA', funding: 'Public', founded: 2011, openRoles: 28, fitRoles: 8, compPercentile: 58, mode: 'Remote', seniorityHeavy: 'Mid–Senior', distinctive: ['Sabbatical program', 'Phone stipend'], topValues: ['Take the long view', 'Raise the bar', 'Test your ideas'], synopsis: 'Duolingo applies game design to language education. Heavy on experimentation culture, A/B testing at massive scale, and ML-driven personalization. Pittsburgh-rooted but remote-friendly across most engineering roles.' },
  { id: 'anthropic.com',      name: 'Anthropic',  logo: 'A',  color: '#cc785c', tagline: 'AI safety research', stage: 'Late-stage', size: 800, hq: 'San Francisco, CA', funding: 'Private · last $61.5B', founded: 2021, openRoles: 65, fitRoles: 14, compPercentile: 85, mode: 'Hybrid', seniorityHeavy: 'Senior–Staff', distinctive: ['Conference budget', 'Research time'], topValues: ['Safety first', 'Empirical rigor', 'Mission-driven'], synopsis: 'Anthropic builds frontier AI systems with a safety research focus. Hiring is aggressive but selective — strong preference for research and engineering depth. Pays at the top of market.' },
  { id: 'getcensus.com',      name: 'Census',     logo: 'C',  color: '#5b21b6', tagline: 'Reverse ETL & data activation', stage: 'Series B', size: 180, hq: 'San Francisco, CA', funding: 'Series B · $60M', founded: 2018, openRoles: 14, fitRoles: 5, compPercentile: 60, mode: 'Hybrid', seniorityHeavy: 'Senior', distinctive: ['Founder-led data team'], topValues: ['Customer-centric', 'Pragmatism', 'Trust'], synopsis: 'Census pioneered the reverse-ETL category — syncing data from warehouses to operational tools. Small, focused team. The data team reports directly to leadership and shapes product.' },
  { id: 'hex.tech',           name: 'Hex',        logo: 'H',  color: '#5a48f0', tagline: 'Modern data workspace', stage: 'Series B', size: 220, hq: 'San Francisco, CA', funding: 'Series B · $52M', founded: 2019, openRoles: 18, fitRoles: 7, compPercentile: 64, mode: 'Remote', seniorityHeavy: 'Senior', distinctive: ['Annual education stipend', 'Internal mentorship'], topValues: ['Curiosity', 'Empathy', 'Build for builders'], synopsis: 'Hex builds a collaborative data workspace that blends notebooks, BI, and AI. Remote-first culture with strong engineering practices. Heavy investment in data tooling, customer-facing builds, and applied ML.' },
  { id: 'tecton.ai',          name: 'Tecton',     logo: 'T',  color: '#0066ff', tagline: 'ML feature platform', stage: 'Series C', size: 180, hq: 'San Francisco, CA', funding: 'Series C · $160M', founded: 2019, openRoles: 22, fitRoles: 9, compPercentile: 68, mode: 'Hybrid', seniorityHeavy: 'Staff', distinctive: ['MLOps focus'], topValues: ['Customer obsession', 'Technical excellence'], synopsis: 'Tecton operationalizes ML features for production. Founders came out of Uber\'s Michelangelo team. Senior-heavy engineering org focused on real-time ML infrastructure.' },
  { id: 'modal.com',          name: 'Modal',      logo: 'M',  color: '#03b384', tagline: 'Cloud for AI workloads', stage: 'Series A', size: 50, hq: 'New York, NY', funding: 'Series A · $16M', founded: 2021, openRoles: 12, fitRoles: 4, compPercentile: 62, mode: 'Remote', seniorityHeavy: 'Staff', distinctive: ['Equity-heavy', 'Founder-led'], topValues: ['Speed', 'Simplicity', 'Customer-first'], synopsis: 'Modal provides serverless cloud infrastructure for AI workloads. Tiny but high-leverage team. Founder-led culture with equity-heavy comp packages and very high autonomy.' },
  { id: 'replicate.com',      name: 'Replicate',  logo: 'R',  color: '#fd5d35', tagline: 'Run open-source models', stage: 'Series B', size: 60, hq: 'Distributed', funding: 'Series B · $40M', founded: 2019, openRoles: 9, fitRoles: 3, compPercentile: 60, mode: 'Remote', seniorityHeavy: 'Senior', distinctive: ['Distributed-first'], topValues: ['Open by default', 'Builder mindset'], synopsis: 'Replicate makes it easy to run open-source ML models in production. Fully distributed team. Strong open-source ethos and a community-driven product approach.' },
  { id: 'linear.app',         name: 'Linear',     logo: 'L',  color: '#5e6ad2', tagline: 'Streamlined issue tracking', stage: 'Series B', size: 60, hq: 'San Francisco, CA', funding: 'Series B · $35M', founded: 2019, openRoles: 8, fitRoles: 2, compPercentile: 70, mode: 'Remote', seniorityHeavy: 'Senior', distinctive: ['Craft-heavy culture'], topValues: ['Quality', 'Speed', 'Magic'], synopsis: 'Linear builds the issue tracker engineering teams actually want to use. Remote-first, craft-obsessed, deliberately small. Hires senior generalists and gives them long runways.' },
  { id: 'retool.com',         name: 'Retool',     logo: 'Re', color: '#3d3d3d', tagline: 'Internal tools, fast', stage: 'Series C', size: 400, hq: 'San Francisco, CA', funding: 'Series C · $130M', founded: 2017, openRoles: 32, fitRoles: 8, compPercentile: 67, mode: 'Hybrid', seniorityHeavy: 'Senior', distinctive: ['Annual education stipend'], topValues: ['Build with rigor', 'Customer empathy'], synopsis: 'Retool is a platform for building internal tools quickly. Hybrid culture, growing engineering team. Heavy on full-stack generalists and customer-facing engineering work.' },
  { id: 'plaid.com',          name: 'Plaid',      logo: 'P',  color: '#0a85ea', tagline: 'Banking infrastructure APIs', stage: 'Late-stage', size: 1500, hq: 'San Francisco, CA', funding: 'Private · last $13.4B', founded: 2013, openRoles: 48, fitRoles: 10, compPercentile: 70, mode: 'Hybrid', seniorityHeavy: 'Senior', distinctive: ['Sabbatical (5yr+)'], topValues: ['Customer trust', 'Long-term thinking'], synopsis: 'Plaid powers the connections between consumer banks and the apps that need them. Mature engineering culture, regulatory rigor, and a strong focus on data security and reliability.' },
  { id: 'snowplow.io',        name: 'Snowplow',   logo: 'Sw', color: '#6638b8', tagline: 'Behavioral data platform', stage: 'Series B', size: 140, hq: 'London, UK', funding: 'Series B · $40M', founded: 2012, openRoles: 16, fitRoles: 5, compPercentile: 55, mode: 'Remote', seniorityHeavy: 'Senior', distinctive: ['Open-source culture'], topValues: ['Open-source first', 'Customer success', 'Curiosity'], synopsis: 'Snowplow builds an open-source behavioral data platform. UK-rooted but US-friendly. Strong engineering culture around event data, with a meaningful open-source contribution model.' },
  { id: 'webflow.com',        name: 'Webflow',    logo: 'W',  color: '#4353ff', tagline: 'Visual web development', stage: 'Late-stage', size: 600, hq: 'San Francisco, CA', funding: 'Series C · $140M', founded: 2013, openRoles: 24, fitRoles: 6, compPercentile: 64, mode: 'Remote', seniorityHeavy: 'Senior', distinctive: ['Remote-first weeks'], topValues: ['Build inclusively', 'Empower creators'], synopsis: 'Webflow lets designers build production websites without code. Fully distributed team. Strong design-tool DNA and an emphasis on accessible product surfaces.' },
  { id: 'sigmacomputing.com', name: 'Sigma',      logo: 'Sg', color: '#1f6feb', tagline: 'Cloud-native analytics', stage: 'Series D', size: 450, hq: 'San Francisco, CA', funding: 'Series D · $200M', founded: 2014, openRoles: 28, fitRoles: 6, compPercentile: 68, mode: 'Hybrid', seniorityHeavy: 'Senior', distinctive: ['Quarterly off-sites'], topValues: ['Customer-first', 'Empower analysts'], synopsis: 'Sigma Computing builds cloud-native BI tools for non-technical users. Hybrid SF culture, mature org. Heavy on analytics engineering and customer-facing data work.' },
];

// User's past employers — seed candidates for Find Like mode.
const WORKED_AT = [
  { id: 'reefai',   name: 'Reef.ai',  logo: 'R',  color: '#1e8a6e', tagline: 'Customer health AI',  stage: 'Series A', size: 60,   mode: 'Hybrid', _profile: 'B2B data, Series A, small team' },
  { id: 'kaskada',  name: 'Kaskada',  logo: 'K',  color: '#5a48f0', tagline: 'ML feature engine',   stage: 'Series A', size: 40,   mode: 'Remote', _profile: 'ML infra, Series A, distributed' },
  { id: 'flyhomes', name: 'Flyhomes', logo: 'F',  color: '#ff6b35', tagline: 'Real estate',         stage: 'Series C', size: 800,  mode: 'Hybrid', _profile: 'Proptech, Series C, hybrid' },
  { id: 'tomo',     name: 'Tomo',     logo: 'T',  color: '#0066ff', tagline: 'Mortgage tech',       stage: 'Series B', size: 200,  mode: 'Hybrid', _profile: 'Proptech, Series B, hybrid' },
  { id: 'remitly',  name: 'Remitly',  logo: 'Rm', color: '#5b21b6', tagline: 'Money transfer',      stage: 'Public',   size: 2500, mode: 'Hybrid', _profile: 'Fintech, Public, hybrid' },
  { id: 'payscale', name: 'PayScale', logo: 'Ps', color: '#3d7a4a', tagline: 'Compensation data',   stage: 'Acquired', size: 600,  mode: 'Hybrid', _profile: 'B2B SaaS, mature, hybrid' },
];

// Headlines clusters — illustrative until the recommender service ships.
// companyIds match COMPANIES[].id (which is the domain).
const CLUSTERS = [
  { id: 'like-reef',           title: 'Companies like Reef.ai',                why: 'Series A–B, B2B data products, small data teams',                companyIds: ['getcensus.com', 'hex.tech', 'modal.com', 'snowplow.io'] },
  { id: 'outcomes',            title: 'Where senior DS folks land offers',    why: 'From application outcomes — small samples, take loosely',         caveat: 'n < 10', companyIds: ['duolingo.com', 'notion.so', 'figma.com', 'stripe.com'] },
  { id: 'shape-match',         title: 'Stage & shape that worked for you',    why: 'Series B, hybrid, 100–250 ppl — your strongest tenures',           companyIds: ['getcensus.com', 'tecton.ai', 'hex.tech', 'sigmacomputing.com', 'retool.com'] },
  { id: 'adjacent-applied',    title: 'Adjacent to your applications',         why: 'Same recruiters, similar JD language, alumni overlap',           companyIds: ['plaid.com', 'linear.app', 'webflow.com', 'retool.com'] },
  { id: 'stretch',             title: 'Stretch picks',                         why: 'Different shape, but your skills could land — worth a look',     companyIds: ['anthropic.com', 'modal.com'] },
  { id: 'distinctive-benefits',title: 'Companies with sabbatical programs',    why: 'Distinctive benefit — under 30% of companies offer this',         companyIds: ['stripe.com', 'duolingo.com', 'plaid.com'] },
];

// ===========================================================================
// SIMILARITY (mock — client-side scoring per the brief)
// ===========================================================================

function computeSimilar(seedIds, candidates, allSeeds) {
  const seeds = seedIds.map((id) => allSeeds.find((s) => s.id === id)).filter(Boolean);
  if (seeds.length === 0) return [];

  const sizes = seeds.map((s) => s.size);
  const avgSize = sizes.reduce((a, b) => a + b, 0) / sizes.length;
  const modes = seeds.map((s) => s.mode);
  const dominantMode = modes.sort(
    (a, b) => modes.filter((v) => v === a).length - modes.filter((v) => v === b).length,
  ).pop();
  const stages = seeds.map((s) => s.stage);

  return candidates
    .map((c) => {
      let score = 0;
      const reasons = [];
      if (stages.includes(c.stage)) { score += 30; reasons.push('same stage'); }
      if (Math.abs(c.size - avgSize) / avgSize < 0.5) { score += 25; reasons.push('similar size'); }
      if (c.mode === dominantMode) { score += 20; reasons.push(`${dominantMode.toLowerCase()} mode`); }
      if (c.fitRoles >= 5) { score += 15; reasons.push(`${c.fitRoles} fit roles`); }
      score += Math.random() * 10;
      return { company: c, score, reasons: reasons.slice(0, 3) };
    })
    .filter((r) => r.score > 30)
    .sort((a, b) => b.score - a.score);
}

// ===========================================================================
// SHARED VISUAL PRIMITIVES
// ===========================================================================

const SamplePill = ({ label = 'Sample data' }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      bgcolor: WARM.accent + '22', color: WARM.accent,
      fontFamily: F, fontWeight: 600, fontSize: 10, letterSpacing: 0.4,
      textTransform: 'uppercase', height: 20,
    }}
  />
);

function CompanyLogo({ company, size = 32 }) {
  return (
    <Box sx={{
      flexShrink: 0,
      width: size, height: size, borderRadius: `${size * 0.25}px`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: company.color + '18', color: company.color,
      fontFamily: F, fontWeight: 700, fontSize: size * 0.42,
    }}>
      {company.logo}
    </Box>
  );
}

function StageBadge({ stage }) {
  const map = {
    'Series A':   { bg: CREAM[300], color: SAGE[700] },
    'Series B':   { bg: CREAM[300], color: SAGE[700] },
    'Series C':   { bg: SAGE[100],  color: SAGE[700] },
    'Series D':   { bg: SAGE[100],  color: SAGE[700] },
    'Late-stage': { bg: SAGE[200],  color: SAGE[800] },
    'Public':     { bg: SAGE[200],  color: SAGE[800] },
  };
  const s = map[stage] || map['Late-stage'];
  return (
    <Chip
      label={stage}
      size="small"
      sx={{
        height: 18, fontFamily: F, fontWeight: 600, fontSize: 9,
        bgcolor: s.bg, color: s.color,
      }}
    />
  );
}

const truncated = {
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

// ===========================================================================
// SELECTION TOGGLE — companies-specific (eye / star cycle)
// undefined → "considering" (eye) → "saved" (star) → undefined
// disqualified is a separate state; not part of the cycle
// ===========================================================================

function CompanyToggle({ state, onCycle, size = 'md' }) {
  const dim = size === 'sm' ? { box: 24, icon: 14 } : { box: 28, icon: 16 };
  const isConsidering = state === 'considering';
  const isSaved = state === 'saved';

  let bg = 'transparent';
  let color = SAGE[300];
  let Icon = VisibilityOutlinedIcon;
  let ringStyle = {};
  let tooltip = 'Click to consider';

  if (isConsidering) {
    bg = SAGE[100]; color = SAGE[600]; Icon = VisibilityIcon;
    tooltip = 'Considering · click to save';
  } else if (isSaved) {
    bg = WARM.accent + '33'; color = WARM.accent; Icon = StarIcon;
    ringStyle = { boxShadow: `0 0 0 1.5px ${WARM.accent}30` };
    tooltip = 'Saved · click to clear';
  }

  return (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onCycle(); }}
        aria-label={tooltip}
        sx={{
          width: dim.box, height: dim.box, borderRadius: 1,
          bgcolor: bg, color, flexShrink: 0,
          transition: 'all 0.15s',
          '&:hover': { bgcolor: state === undefined ? SAGE[100] : bg },
          ...ringStyle,
        }}
      >
        <Icon sx={{ fontSize: dim.icon }} />
      </IconButton>
    </Tooltip>
  );
}

// ===========================================================================
// COMPANY MINI — used in the right rail
// ===========================================================================

function CompanyMini({ company, state, onCycle, onClick, onUndoDQ }) {
  const isDQ = state === 'disqualified';
  return (
    <Paper
      variant="outlined"
      onClick={isDQ ? undefined : onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.5, py: 1.25, borderRadius: 3,
        bgcolor: isDQ ? SAGE[100] + '50' : CREAM[50],
        borderColor: SAGE[200],
        opacity: isDQ ? 0.55 : 1,
        cursor: isDQ ? 'default' : 'pointer',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: isDQ ? SAGE[200] : SAGE[300] },
      }}
    >
      <CompanyLogo company={company} size={26} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontFamily: F, fontSize: 12, fontWeight: 600, color: SAGE[800],
          textDecoration: isDQ ? 'line-through' : 'none',
          ...truncated,
        }}>
          {company.name}
        </Typography>
        <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400], ...truncated }}>
          {isDQ ? 'Disqualified' : `${company.fitRoles} fit · ${company.openRoles} open`}
        </Typography>
      </Box>
      {isDQ ? (
        <Box
          component="button"
          onClick={(e) => { e.stopPropagation(); onUndoDQ(company.id); }}
          sx={{
            flexShrink: 0, fontFamily: F, fontSize: 10, fontWeight: 500,
            px: 1, py: 0.5, borderRadius: 1, color: SAGE[500],
            background: 'transparent', border: 'none', cursor: 'pointer',
            '&:hover': { bgcolor: SAGE[100] },
          }}
        >
          Undo
        </Box>
      ) : (
        <CompanyToggle state={state} onCycle={() => onCycle(company.id)} size="sm" />
      )}
    </Paper>
  );
}

// ===========================================================================
// COMPANY CARD — main grid card across all modes
// ===========================================================================

function CompanyCard({ company, state, onCycle, onDisqualify, onClick }) {
  const [showActions, setShowActions] = useState(false);
  const isDQ = state === 'disqualified';
  const isSaved = state === 'saved';

  if (isDQ) {
    return (
      <Paper variant="outlined" sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        p: 2, borderRadius: 3, minHeight: 88,
        bgcolor: SAGE[100] + '40',
        borderStyle: 'dashed', borderColor: SAGE[300], borderWidth: '1.5px',
        opacity: 0.7,
      }}>
        <CompanyLogo company={company} size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontFamily: F, fontSize: 13, fontWeight: 600, color: SAGE[600],
            textDecoration: 'line-through', ...truncated,
          }}>
            {company.name}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[400] }}>
            Disqualified
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); onCycle(company.id); }}
          sx={{
            fontFamily: F, fontSize: 11, fontWeight: 500, color: SAGE[600],
            bgcolor: SAGE[100], px: 1.25, py: 0.5, minWidth: 0, borderRadius: 1,
            '&:hover': { bgcolor: SAGE[200] },
          }}
        >
          Undo
        </Button>
      </Paper>
    );
  }

  // Two-line tagline clamp — gives cards consistent height even when
  // tagline wraps short, and lets longer taglines breathe.
  const taglineClamp = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      sx={{
        position: 'relative', cursor: 'pointer',
        p: 2.5, borderRadius: 3, minHeight: 220,
        display: 'flex', flexDirection: 'column',
        bgcolor: CREAM[50],
        borderColor: isSaved ? WARM.accent + '88' : SAGE[200], borderWidth: '1.5px',
        transition: 'all 0.15s',
        '&:hover': { borderColor: SAGE[400], transform: 'translateY(-1px)' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
        <CompanyLogo company={company} size={44} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: F, fontSize: 16, fontWeight: 600, color: SAGE[800], ...truncated }}>
            {company.name}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 12, color: SAGE[500], lineHeight: 1.45, mt: 0.25, ...taglineClamp }}>
            {company.tagline}
          </Typography>
        </Box>
        <CompanyToggle state={state} onCycle={() => onCycle(company.id)} />
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5, mb: 'auto' }}>
        <StageBadge stage={company.stage} />
        <Chip label={company.mode} size="small" sx={{
          height: 20, fontFamily: F, fontWeight: 600, fontSize: 10,
          bgcolor: SAGE[100], color: SAGE[600],
        }} />
        <Chip
          label={`${company.size < 100 ? '<100' : company.size < 500 ? '100–500' : company.size < 2000 ? '500–2k' : '2k+'} ppl`}
          size="small"
          sx={{ height: 20, fontFamily: F, fontWeight: 600, fontSize: 10, bgcolor: SAGE[100], color: SAGE[600] }}
        />
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{
        pt: 1.5, mt: 1.5, borderTop: 1, borderColor: SAGE[200],
      }}>
        <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500] }}>
          <Box component="span" sx={{ fontWeight: 600, color: SAGE[700] }}>{company.fitRoles}</Box>
          {' '}fit · {company.openRoles} open
        </Typography>
        <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500] }}>
          comp p{company.compPercentile}
        </Typography>
      </Stack>

      {showActions && state !== 'saved' && (
        <Tooltip title="Disqualify (hide from defaults)">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDisqualify(company.id); }}
            aria-label="Disqualify"
            sx={{
              position: 'absolute', top: 18, right: 56,
              width: 24, height: 24, borderRadius: 1,
              bgcolor: CREAM[50], color: SAGE[400],
              border: 1, borderColor: SAGE[200],
              '&:hover': { bgcolor: WARM.rose + '22', color: WARM.rose, borderColor: WARM.rose },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}

function FindLikeResultCard({ company, state, onCycle, onClick, score, reasons }) {
  const isSaved = state === 'saved';
  const taglineClamp = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        cursor: 'pointer', p: 2.5, borderRadius: 3, minHeight: 220,
        display: 'flex', flexDirection: 'column',
        bgcolor: CREAM[50],
        borderColor: isSaved ? WARM.accent + '88' : SAGE[200], borderWidth: '1.5px',
        transition: 'all 0.15s',
        '&:hover': { borderColor: SAGE[400], transform: 'translateY(-1px)' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
        <CompanyLogo company={company} size={44} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: F, fontSize: 16, fontWeight: 600, color: SAGE[800], ...truncated }}>
            {company.name}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 12, color: SAGE[500], lineHeight: 1.45, mt: 0.25, ...taglineClamp }}>
            {company.tagline}
          </Typography>
        </Box>
        <CompanyToggle state={state} onCycle={() => onCycle(company.id)} />
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5, mb: 'auto' }}>
        <StageBadge stage={company.stage} />
        <Chip label={company.mode} size="small" sx={{
          height: 20, fontFamily: F, fontWeight: 600, fontSize: 10,
          bgcolor: SAGE[100], color: SAGE[600],
        }} />
      </Stack>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{
        pt: 1.5, mt: 1.5, borderTop: 1, borderColor: SAGE[200], gap: 1,
      }}>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
          {reasons.map((r, i) => (
            <Stack key={i} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: SAGE[500] }} />
              <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[600] }}>
                {r}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Typography sx={{ fontFamily: F, fontSize: 11, fontWeight: 600, color: SAGE[600], fontVariantNumeric: 'tabular-nums' }}>
          match {Math.round(score)}
        </Typography>
      </Stack>
    </Paper>
  );
}

// ===========================================================================
// DETAIL MODAL — uses our generic EntityDetailModal frame, fills slots
// ===========================================================================

function deriveRoles(company) {
  if (company.size > 1000) {
    return [
      { name: 'Engineering',    pct: 38, color: SAGE[600] },
      { name: 'Data & ML',      pct: 18, color: SAGE[500] },
      { name: 'Product',        pct: 14, color: WARM.accent },
      { name: 'Sales & GTM',    pct: 12, color: CREAM[400] },
      { name: 'Design',         pct: 8,  color: SAGE[400] },
      { name: 'Other',          pct: 10, color: SAGE[300] },
    ];
  }
  return [
    { name: 'Engineering', pct: 52, color: SAGE[600] },
    { name: 'Data & ML',   pct: 22, color: SAGE[500] },
    { name: 'Product',     pct: 10, color: WARM.accent },
    { name: 'Sales & GTM', pct: 8,  color: CREAM[400] },
    { name: 'Other',       pct: 8,  color: SAGE[300] },
  ];
}

function ModalStackedBar({ items, height = 5 }) {
  return (
    <Box sx={{ display: 'flex', width: '100%', borderRadius: 9999, overflow: 'hidden', height }}>
      {items.map((item) => (
        <Box key={item.name} title={`${item.name}: ${item.pct}%`} sx={{ width: `${item.pct}%`, bgcolor: item.color }} />
      ))}
    </Box>
  );
}

function ModalFitTile({ label, headlineStat, headlineLabel, items, footer }) {
  return (
    <Paper variant="outlined" sx={{
      borderRadius: 3, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: '1.5px',
    }}>
      <Box sx={{ px: 2, py: 1.5, flex: 1 }}>
        <Typography sx={{
          fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[400],
          textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75,
        }}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontFamily: SERIF, fontSize: 20, fontWeight: 700, color: SAGE[800], fontVariantNumeric: 'tabular-nums' }}>
            {headlineStat}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500] }}>
            {headlineLabel}
          </Typography>
        </Stack>
        {items && (
          <>
            <ModalStackedBar items={items} />
            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.25 }}>
              {items.slice(0, 2).map((item) => (
                <Stack key={item.name} direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: item.color }} />
                  <Typography sx={{ fontFamily: F, fontSize: 9, color: SAGE[500] }}>
                    {item.name} {item.pct}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Box>
      {footer && (
        <Box sx={{ px: 2, py: 1, bgcolor: SAGE[100], borderTop: 1, borderColor: SAGE[200] }}>
          <Typography sx={{ fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[700], lineHeight: 1.3 }}>
            {footer}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function CompanyDetailContent({ company }) {
  const roles = deriveRoles(company);
  const top = (arr) => arr.find((s) => s.pct === Math.max(...arr.map((x) => x.pct))) || arr[0];
  const seniorityMock = company.seniorityHeavy.includes('Staff')
    ? [
      { name: 'Junior',     pct: 5,  color: SAGE[300] },
      { name: 'Mid',        pct: 15, color: SAGE[400] },
      { name: 'Senior',     pct: 35, color: SAGE[500] },
      { name: 'Staff',      pct: 30, color: SAGE[600] },
      { name: 'Principal+', pct: 15, color: SAGE[700] },
    ]
    : [
      { name: 'Junior',     pct: 10, color: SAGE[300] },
      { name: 'Mid',        pct: 25, color: SAGE[400] },
      { name: 'Senior',     pct: 42, color: SAGE[500] },
      { name: 'Staff',      pct: 18, color: SAGE[600] },
      { name: 'Principal+', pct: 5,  color: SAGE[700] },
    ];
  const locationsMock = company.mode === 'Remote' || company.mode === 'Remote-friendly'
    ? [
      { name: 'Remote (US)',                pct: 48, color: CREAM[400] },
      { name: company.hq.split(',')[0],     pct: 24, color: SAGE[600] },
      { name: 'New York',                   pct: 14, color: SAGE[500] },
      { name: 'Other',                      pct: 14, color: SAGE[300] },
    ]
    : [
      { name: company.hq.split(',')[0], pct: 42, color: SAGE[600] },
      { name: 'New York',               pct: 18, color: SAGE[500] },
      { name: 'Remote (US)',            pct: 22, color: CREAM[400] },
      { name: 'Other',                  pct: 18, color: SAGE[300] },
    ];

  return (
    <>
      {/* Synopsis */}
      <Typography sx={{ fontFamily: F, fontSize: 14, color: SAGE[600], lineHeight: 1.7, mb: 3 }}>
        {company.synopsis}
      </Typography>

      {/* Fit strip */}
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Typography sx={{ fontFamily: SERIF, fontSize: 14, fontWeight: 600, color: SAGE[800] }}>
          Your fit at a glance
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <SamplePill />
          <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400] }}>
            Across all {company.openRoles} roles
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25, mb: 3 }}>
        <ModalFitTile label="Roles"    headlineStat={`${company.fitRoles}`}                   headlineLabel="you'd fit" items={roles} footer={`${roles[0].name}-heavy`} />
        <ModalFitTile label="Locations" headlineStat={`${locationsMock[0].pct}%`}             headlineLabel={locationsMock[0].name} items={locationsMock} footer={company.mode} />
        <ModalFitTile label="Seniority" headlineStat={`${top(seniorityMock).pct}%`}           headlineLabel={top(seniorityMock).name} items={seniorityMock} footer={`${company.seniorityHeavy} heavy`} />
        <ModalFitTile label="Comp"      headlineStat={`p${company.compPercentile}`}            headlineLabel="vs market" footer={company.compPercentile >= 70 ? 'Above market' : company.compPercentile >= 55 ? 'At market' : 'Below market'} />
      </Box>

      {/* Two-column values + benefits */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ borderRadius: 3, px: 2, py: 1.75, bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: '1.5px' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontFamily: F, fontSize: 11, fontWeight: 600, color: SAGE[600], textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Stated values
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 9, color: SAGE[400] }}>Top 3</Typography>
          </Stack>
          <Stack spacing={0.75}>
            {company.topValues.map((v, i) => (
              <Stack key={v} direction="row" spacing={1} alignItems="center">
                <Box sx={{
                  flexShrink: 0, width: 16, height: 16, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: SAGE[100], color: SAGE[600],
                  fontFamily: F, fontSize: 9, fontWeight: 700,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontFamily: F, fontSize: 12, color: SAGE[700] }}>{v}</Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 3, px: 2, py: 1.75, bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: '1.5px' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontFamily: F, fontSize: 11, fontWeight: 600, color: SAGE[600], textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Distinctive benefits
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 9, color: SAGE[400] }}>{'<30% of cos'}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
            {company.distinctive.length === 0 ? (
              <Typography sx={{ fontFamily: F, fontSize: 11, fontStyle: 'italic', color: SAGE[400] }}>
                None flagged
              </Typography>
            ) : company.distinctive.map((b) => (
              <Chip key={b} label={b} size="small" sx={{
                height: 22, fontFamily: F, fontWeight: 500, fontSize: 11,
                bgcolor: WARM.accent + '33', color: WARM.accent,
              }} />
            ))}
          </Stack>
        </Paper>
      </Box>
    </>
  );
}

function CompanyDetailModal({ company, onClose, onCycle, onDisqualify }) {
  const { getState } = useSelections('companies');
  if (!company) return null;
  const state = getState(company.id);
  const isDQ = state === 'disqualified';

  // company.id is the domain — navigates to the live detail page.
  const handleViewMore = () => {
    window.open(`/explore/companies/${company.id}`, '_blank', 'noopener,noreferrer');
  };

  const header = (
    <Stack direction="row" alignItems="flex-start" spacing={2}>
      <Box sx={{
        flexShrink: 0, width: 56, height: 56, borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: company.color, color: '#fff',
        fontFamily: F, fontWeight: 700, fontSize: 22,
      }}>
        {company.logo}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
          <Typography sx={{ fontFamily: SERIF, fontSize: 26, fontWeight: 600, color: SAGE[800] }}>
            {company.name}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 12, color: SAGE[400] }}>
            {company.tagline}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5, fontFamily: F, fontSize: 11, color: SAGE[500] }}>
          <span>{company.stage}</span><span style={{ color: SAGE[300] }}>·</span>
          <span>{company.size.toLocaleString()} employees</span><span style={{ color: SAGE[300] }}>·</span>
          <span>Founded {company.founded}</span><span style={{ color: SAGE[300] }}>·</span>
          <span>{company.hq}</span><span style={{ color: SAGE[300] }}>·</span>
          <span>{company.funding}</span>
        </Stack>
      </Box>
      <Box sx={{ flexShrink: 0, px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: SAGE[100] }}>
        <Typography sx={{ fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[700] }}>
          {company.openRoles} open roles
        </Typography>
      </Box>
    </Stack>
  );

  const selectionLabel = state === 'saved'
    ? 'Saved'
    : state === 'considering'
    ? 'Considering'
    : isDQ
    ? 'Disqualified'
    : 'Consider';

  return (
    <EntityDetailModal
      open
      onClose={onClose}
      maxWidth="md"
      header={header}
      body={<CompanyDetailContent company={company} />}
      footerSelectionArea={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            onClick={() => onCycle(company.id)}
            variant={state === 'saved' || state === 'considering' ? 'contained' : 'outlined'}
            startIcon={state === 'saved' ? <StarIcon /> : state === 'considering' ? <VisibilityIcon /> : <VisibilityOutlinedIcon />}
            sx={{
              fontFamily: F, fontWeight: 600, fontSize: 13, borderRadius: 2.5,
              ...(state === 'saved'
                ? { bgcolor: WARM.accent, color: CREAM[50], '&:hover': { bgcolor: '#a87a4f' } }
                : state === 'considering'
                ? { bgcolor: SAGE[600], color: CREAM[50], '&:hover': { bgcolor: SAGE[700] } }
                : { borderColor: SAGE[300], color: SAGE[700] }),
            }}
          >
            {selectionLabel}
          </Button>
          {!isDQ && state !== 'saved' && (
            <Button
              onClick={() => onDisqualify(company.id)}
              variant="outlined"
              size="small"
              sx={{
                fontFamily: F, fontWeight: 500, fontSize: 12, borderRadius: 2.5,
                color: SAGE[500], borderColor: SAGE[200],
                '&:hover': { color: WARM.rose, borderColor: WARM.rose, bgcolor: WARM.rose + '11' },
              }}
            >
              Not for me
            </Button>
          )}
          {isDQ && (
            <Button
              onClick={() => onCycle(company.id)}
              variant="outlined"
              size="small"
              sx={{ fontFamily: F, fontWeight: 500, fontSize: 12, borderRadius: 2.5, color: SAGE[500], borderColor: SAGE[200] }}
            >
              Undo disqualify
            </Button>
          )}
        </Stack>
      }
      footerPrimaryAction={
        <Button
          onClick={handleViewMore}
          variant="contained"
          endIcon={<OpenInNewIcon />}
          sx={{
            fontFamily: F, fontWeight: 600, fontSize: 13, borderRadius: 2.5,
            bgcolor: SAGE[700], '&:hover': { bgcolor: SAGE[800] },
          }}
        >
          View company page
        </Button>
      }
    />
  );
}

// ===========================================================================
// SELECTION RAIL — companies-specific (matches mockup styling)
// ===========================================================================

function CompaniesRail({ onClick, onCompare }) {
  const { listType, cycle, undo, setState } = useSelections('companies');
  const [showDQ, setShowDQ] = useState(false);

  const byId = useMemo(() => {
    const m = new Map();
    for (const c of COMPANIES) m.set(c.id, c);
    return m;
  }, []);

  const buckets = useMemo(() => {
    const out = { considering: [], saved: [], disqualified: [] };
    for (const [id, state] of listType()) {
      const c = byId.get(id);
      if (!c || !out[state]) continue;
      out[state].push(c);
    }
    return out;
  }, [byId, listType]);

  const considering = buckets.considering;
  const saved = buckets.saved;
  const disqualified = buckets.disqualified;
  const consideringFit = considering.reduce((s, c) => s + c.fitRoles, 0);
  const savedFit = saved.reduce((s, c) => s + c.fitRoles, 0);

  const handleUndoDQ = (id) => undo(id);

  return (
    <Stack spacing={1.5} sx={{ position: 'sticky', top: 24 }}>
      {/* Considering */}
      <Paper variant="outlined" sx={{
        borderRadius: 4, overflow: 'hidden',
        bgcolor: CREAM[50],
        borderColor: considering.length >= 2 ? SAGE[400] : SAGE[200], borderWidth: 2,
        transition: 'border-color 0.2s',
      }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: SAGE[200], bgcolor: SAGE[100] }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              width: 24, height: 24, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: SAGE[600], color: CREAM[50],
            }}>
              <VisibilityIcon sx={{ fontSize: 12 }} />
            </Box>
            <Typography sx={{ fontFamily: F, fontWeight: 600, fontSize: 14, color: SAGE[800] }}>
              Considering
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 12, color: SAGE[500], ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {considering.length}
            </Typography>
          </Stack>
          <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[500], mt: 0.25 }}>
            Cohort for side-by-side comparison · {consideringFit} fit roles
          </Typography>
        </Box>
        {considering.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: F, fontSize: 12, fontWeight: 500, color: SAGE[600] }}>
              Nothing to compare yet
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400], lineHeight: 1.6, mt: 0.25 }}>
              Click the eye on any company to add it to your cohort.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 1.25, py: 1.25, maxHeight: 280, overflowY: 'auto' }}>
            <Stack spacing={0.75}>
              {considering.map((c) => (
                <CompanyMini key={c.id} company={c} state="considering" onCycle={cycle} onClick={() => onClick(c)} onUndoDQ={handleUndoDQ} />
              ))}
            </Stack>
          </Box>
        )}
        {considering.length >= 2 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: SAGE[200], bgcolor: SAGE[100] + '80' }}>
            <Button
              fullWidth
              onClick={onCompare}
              variant="contained"
              endIcon={<ChevronRightIcon />}
              sx={{
                fontFamily: F, fontWeight: 600, fontSize: 12, borderRadius: 1.5,
                bgcolor: SAGE[700], '&:hover': { bgcolor: SAGE[800] },
              }}
            >
              Compare these {considering.length} side-by-side
            </Button>
            <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[500], textAlign: 'center', mt: 1 }}>
              Best with 2–5 companies · 6+ shows as table
            </Typography>
          </Box>
        )}
        {considering.length === 1 && (
          <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: SAGE[200], bgcolor: CREAM[200] + '60' }}>
            <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[500], textAlign: 'center' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Add 1 more</Box> to start comparing
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Saved */}
      <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: 2 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: SAGE[200], bgcolor: WARM.accent + '33' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              width: 24, height: 24, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: WARM.accent, color: CREAM[50],
            }}>
              <StarIcon sx={{ fontSize: 12 }} />
            </Box>
            <Typography sx={{ fontFamily: F, fontWeight: 600, fontSize: 14, color: SAGE[800] }}>
              Saved
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 12, color: SAGE[500], ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {saved.length}
            </Typography>
          </Stack>
          <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[500], mt: 0.25 }}>
            Long-term watchlist · {savedFit} fit roles · weekly digest
          </Typography>
        </Box>
        {saved.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: F, fontSize: 12, fontWeight: 500, color: SAGE[600] }}>
              No saved companies
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400], lineHeight: 1.6, mt: 0.25 }}>
              Click again on the eye to save (star). Saved companies appear in your weekly digest.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 1.25, py: 1.25, maxHeight: 280, overflowY: 'auto' }}>
            <Stack spacing={0.75}>
              {saved.map((c) => (
                <CompanyMini key={c.id} company={c} state="saved" onCycle={cycle} onClick={() => onClick(c)} onUndoDQ={handleUndoDQ} />
              ))}
            </Stack>
          </Box>
        )}
        {saved.length > 0 && (
          <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: SAGE[200] }}>
            <Button fullWidth size="small" sx={{ fontFamily: F, fontSize: 11, fontWeight: 500, color: SAGE[500] }}>
              View all open roles ({savedFit}) →
            </Button>
          </Box>
        )}
      </Paper>

      {/* Disqualified */}
      {disqualified.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: SAGE[100] + '50', borderColor: SAGE[200] }}>
          <Box
            component="button"
            onClick={() => setShowDQ((x) => !x)}
            sx={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 1.75, py: 1, fontFamily: F, fontSize: 11, color: SAGE[500],
              background: 'transparent', border: 'none', cursor: 'pointer',
            }}
          >
            <Box>
              <Box component="span" sx={{ fontWeight: 600 }}>{disqualified.length}</Box> disqualified
            </Box>
            <ChevronRightIcon sx={{ fontSize: 14, transform: showDQ ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
          </Box>
          {showDQ && (
            <Box sx={{ px: 1.25, pb: 1.25 }}>
              <Stack spacing={0.75}>
                {disqualified.map((c) => (
                  <CompanyMini key={c.id} company={c} state="disqualified" onCycle={cycle} onClick={() => onClick(c)} onUndoDQ={handleUndoDQ} />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {considering.length === 0 && saved.length === 0 && (
        <Box sx={{ px: 1.5, py: 1.25, borderRadius: 3, bgcolor: CREAM[200] + 'cc', border: 1, borderColor: CREAM[300] }}>
          <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[600], lineHeight: 1.7 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>How this works:</Box>
            {' '}Click <VisibilityIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: SAGE[600] }} /> to consider · click again for{' '}
            <StarIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: WARM.accent }} /> to save.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// ===========================================================================
// MODES
// ===========================================================================

function ModeSearch({ onCompanyClick }) {
  const { getState, cycle, setState } = useSelections('companies');
  const [query, setQuery] = useState('');
  const [recent] = useState(['Stripe', 'Anthropic', 'Linear']);

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    return COMPANIES
      .filter((c) => getState(c.id) !== 'disqualified')
      .filter((c) => c.name.toLowerCase().includes(q) || c.tagline.toLowerCase().includes(q))
      .slice(0, 12);
  }, [query, getState]);

  const handleDisqualify = (id) => setState(id, 'disqualified');

  return (
    <Box>
      <Paper variant="outlined" sx={{
        p: 3, mb: 2.5, borderRadius: 4,
        bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: 2,
      }}>
        <Typography sx={{ fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[500], textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Search by company name
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <Box
            component="input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Stripe, Anthropic, Notion…"
            sx={{
              width: '100%', fontSize: 16, px: 2, py: 1.5, pl: 5,
              borderRadius: 2.5, border: '1.5px solid', borderColor: SAGE[200],
              fontFamily: F, color: SAGE[800], bgcolor: '#fff',
              outline: 'none',
              '&:focus': { borderColor: SAGE[500] },
            }}
          />
          <SearchIcon sx={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: SAGE[400] }} />
          {query && (
            <Button
              size="small"
              onClick={() => setQuery('')}
              sx={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                fontFamily: F, fontSize: 10, fontWeight: 500, color: SAGE[500],
                bgcolor: SAGE[100], minWidth: 0, px: 1, py: 0.5, borderRadius: 1,
                '&:hover': { bgcolor: SAGE[200] },
              }}
            >
              Clear
            </Button>
          )}
        </Box>
        {!query && recent.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[400], textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
              Recent
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
              {recent.map((r) => (
                <Chip
                  key={r}
                  label={r}
                  size="small"
                  onClick={() => setQuery(r)}
                  sx={{
                    height: 24, fontFamily: F, fontWeight: 500, fontSize: 11,
                    bgcolor: SAGE[100], color: SAGE[700], cursor: 'pointer',
                    '&:hover': { bgcolor: SAGE[200] },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {query.trim() && (
        <Box>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }}>
            <Typography sx={{ fontFamily: SERIF, fontSize: 14, fontWeight: 600, color: SAGE[800] }}>
              {filtered.length === 0 ? 'No matches' : `${filtered.length} ${filtered.length === 1 ? 'match' : 'matches'}`}
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400] }}>
              for "{query}"
            </Typography>
          </Stack>
          {filtered.length === 0 ? (
            <Paper variant="outlined" sx={{
              p: 4, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed',
              bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: 2,
            }}>
              <Typography sx={{ fontFamily: F, fontSize: 14, fontWeight: 500, color: SAGE[600], mb: 0.5 }}>
                No companies matched
              </Typography>
              <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[400], lineHeight: 1.6, maxWidth: 360, mx: 'auto' }}>
                Try the Headlines or Find Like modes to discover companies you might not know to search for.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
              {filtered.map((c) => (
                <CompanyCard
                  key={c.id}
                  company={c}
                  state={getState(c.id)}
                  onCycle={cycle}
                  onDisqualify={handleDisqualify}
                  onClick={() => onCompanyClick(c)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {!query.trim() && (
        <Paper variant="outlined" sx={{
          p: 5, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed',
          bgcolor: CREAM[200] + '50', borderColor: SAGE[200],
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%', mx: 'auto', mb: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: SAGE[100],
          }}>
            <SearchIcon sx={{ fontSize: 18, color: SAGE[500] }} />
          </Box>
          <Typography sx={{ fontFamily: F, fontSize: 14, fontWeight: 500, color: SAGE[700], mb: 0.5 }}>
            Looking for someone specific?
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500], lineHeight: 1.6, maxWidth: 360, mx: 'auto' }}>
            Type a company name to jump straight to them. For exploring what's out there, switch to{' '}
            <Box component="span" sx={{ fontWeight: 600, color: SAGE[700] }}>Headlines</Box> or{' '}
            <Box component="span" sx={{ fontWeight: 600, color: SAGE[700] }}>Find Like</Box>.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

function HeadlineRow({ cluster, onCompanyClick }) {
  const { getState, cycle, setState } = useSelections('companies');
  const handleDisqualify = (id) => setState(id, 'disqualified');

  const clusterCompanies = cluster.companyIds
    .map((id) => COMPANIES.find((c) => c.id === id))
    .filter(Boolean)
    .filter((c) => getState(c.id) !== 'disqualified')
    .slice(0, 4);

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5, px: 0.5, flexWrap: 'wrap' }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
          <Typography sx={{ fontFamily: SERIF, fontSize: 16, fontWeight: 600, color: SAGE[800] }}>
            {cluster.title}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500] }}>
            {cluster.why}
          </Typography>
          {cluster.caveat && (
            <Chip label={cluster.caveat} size="small" sx={{
              height: 18, fontFamily: F, fontWeight: 600, fontSize: 9,
              letterSpacing: 0.5, textTransform: 'uppercase',
              bgcolor: WARM.accent + '33', color: WARM.accent,
            }} />
          )}
        </Stack>
        <Button size="small" sx={{ fontFamily: F, fontSize: 11, fontWeight: 500, color: SAGE[500] }}>
          See all →
        </Button>
      </Stack>
      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: `repeat(${Math.max(clusterCompanies.length, 1)}, minmax(0, 1fr))`,
      }}>
        {clusterCompanies.map((c) => (
          <CompanyCard
            key={c.id}
            company={c}
            state={getState(c.id)}
            onCycle={cycle}
            onDisqualify={handleDisqualify}
            onClick={() => onCompanyClick(c)}
          />
        ))}
      </Box>
    </Box>
  );
}

function ModeHeadlines({ onCompanyClick }) {
  const headlines = CLUSTERS.filter((cl) => cl.companyIds.length >= 2).slice(0, 5);
  return (
    <Stack spacing={4}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
        <SamplePill label="Cluster recs are illustrative" />
        <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500] }}>
          Headlines come from the recommender service when it ships. Today: hand-picked clusters.
        </Typography>
      </Stack>
      {headlines.map((cluster) => (
        <HeadlineRow key={cluster.id} cluster={cluster} onCompanyClick={onCompanyClick} />
      ))}
    </Stack>
  );
}

function SeedSlot({ seed, allSeeds, onPick, onClear, slotIndex }) {
  const [picking, setPicking] = useState(false);
  const [pickerTab, setPickerTab] = useState('worked');
  const [pickerQuery, setPickerQuery] = useState('');

  if (seed) {
    return (
      <Paper variant="outlined" sx={{
        p: 1.5, borderRadius: 3,
        display: 'flex', alignItems: 'center', gap: 1.25,
        bgcolor: CREAM[50], borderColor: SAGE[400], borderWidth: 2,
      }}>
        <Box sx={{
          flexShrink: 0, width: 32, height: 32, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: seed.color, color: '#fff',
          fontFamily: F, fontWeight: 700, fontSize: 13,
        }}>
          {seed.logo}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: F, fontSize: 12, fontWeight: 600, color: SAGE[800], ...truncated }}>
            {seed.name}
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400], ...truncated }}>
            {seed._profile || seed.tagline}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => onClear(slotIndex)} aria-label="Remove" sx={{ flexShrink: 0, color: SAGE[400] }}>
          <CloseIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Paper>
    );
  }

  if (picking) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, bgcolor: CREAM[50], borderColor: SAGE[300], borderWidth: '1.5px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.25, p: 0.25, borderRadius: 0.75, bgcolor: SAGE[100] }}>
            {[
              { id: 'worked', label: 'Worked at' },
              { id: 'saved',  label: 'Saved' },
              { id: 'search', label: 'Search' },
            ].map((t) => (
              <Box
                key={t.id}
                component="button"
                onClick={() => setPickerTab(t.id)}
                sx={{
                  fontFamily: F, fontSize: 10, fontWeight: 500,
                  px: 1, py: 0.25, borderRadius: 0.5, cursor: 'pointer', border: 'none',
                  bgcolor: pickerTab === t.id ? CREAM[50] : 'transparent',
                  color: pickerTab === t.id ? SAGE[800] : SAGE[500],
                }}
              >
                {t.label}
              </Box>
            ))}
          </Box>
          <Button size="small" onClick={() => setPicking(false)} sx={{ fontFamily: F, fontSize: 10, color: SAGE[400], minWidth: 0 }}>
            Cancel
          </Button>
        </Stack>
        {pickerTab === 'search' && (
          <Box
            component="input"
            autoFocus
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            placeholder="Search companies…"
            sx={{
              width: '100%', fontSize: 11, px: 1.25, py: 0.75, mb: 1,
              borderRadius: 0.75, border: 1, borderColor: SAGE[200],
              fontFamily: F, color: SAGE[800], bgcolor: '#fff', outline: 'none',
            }}
          />
        )}
        <Stack spacing={0.5} sx={{ maxHeight: 176, overflowY: 'auto' }}>
          {allSeeds[pickerTab]
            .filter((s) => !pickerQuery || s.name.toLowerCase().includes(pickerQuery.toLowerCase()))
            .map((s) => (
              <Box
                key={s.id}
                component="button"
                onClick={() => { onPick(slotIndex, s); setPicking(false); }}
                sx={{
                  width: '100%', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 0.75, py: 0.75, borderRadius: 0.75, cursor: 'pointer',
                  background: 'transparent', border: 'none',
                  '&:hover': { bgcolor: SAGE[100] },
                }}
              >
                <Box sx={{
                  flexShrink: 0, width: 22, height: 22, borderRadius: 0.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: s.color, color: '#fff',
                  fontFamily: F, fontWeight: 700, fontSize: 10,
                }}>
                  {s.logo}
                </Box>
                <Typography sx={{ fontFamily: F, fontSize: 11, fontWeight: 500, color: SAGE[700], ...truncated }}>
                  {s.name}
                </Typography>
              </Box>
            ))}
          {allSeeds[pickerTab].length === 0 && (
            <Typography sx={{ fontFamily: F, fontSize: 10, fontStyle: 'italic', color: SAGE[400], px: 1, py: 1 }}>
              None available — try another tab
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Box
      component="button"
      onClick={() => setPicking(true)}
      sx={{
        width: '100%', minHeight: 60, p: 1.5, borderRadius: 3, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
        background: 'transparent', border: '1.5px dashed', borderColor: SAGE[300],
        transition: 'all 0.15s',
        '&:hover': { bgcolor: SAGE[100], borderColor: SAGE[400] },
      }}
    >
      <AddIcon sx={{ fontSize: 14, color: SAGE[500] }} />
      <Typography sx={{ fontFamily: F, fontSize: 11, fontWeight: 500, color: SAGE[500] }}>
        Add seed {slotIndex + 1}
      </Typography>
    </Box>
  );
}

function ModeFindLike({ onCompanyClick }) {
  const { getState, cycle, setState, listType } = useSelections('companies');
  const [seeds, setSeeds] = useState([
    WORKED_AT.find((c) => c.id === 'reefai'),
    WORKED_AT.find((c) => c.id === 'kaskada'),
    null,
    null,
  ]);

  const savedCompanies = useMemo(() => {
    return listType()
      .filter(([, s]) => s === 'saved' || s === 'considering')
      .map(([id]) => COMPANIES.find((c) => c.id === id))
      .filter(Boolean);
  }, [listType]);

  const allSeeds = { worked: WORKED_AT, saved: savedCompanies, search: COMPANIES };

  const onPick = (slotIndex, seed) => setSeeds((s) => s.map((x, i) => i === slotIndex ? seed : x));
  const onClear = (slotIndex) => setSeeds((s) => s.map((x, i) => i === slotIndex ? null : x));

  const filledSeeds = seeds.filter(Boolean);
  const seedIds = filledSeeds.map((s) => s.id);
  const allKnown = [...WORKED_AT, ...COMPANIES];

  const results = useMemo(() => {
    if (filledSeeds.length === 0) return [];
    return computeSimilar(
      seedIds,
      COMPANIES.filter((c) => !seedIds.includes(c.id) && getState(c.id) !== 'disqualified'),
      allKnown,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedIds.join(','), getState]);

  const stages = filledSeeds.map((s) => s.stage);
  const modes = filledSeeds.map((s) => s.mode);
  const isScattered = filledSeeds.length >= 3 && (
    new Set(stages).size === filledSeeds.length || new Set(modes).size >= 3
  );
  const sizes = filledSeeds.map((s) => s.size);
  const avgSize = sizes.length ? Math.round(sizes.reduce((a, b) => a + b, 0) / sizes.length) : 0;

  return (
    <Box>
      <Paper variant="outlined" sx={{
        p: 2.5, mb: 2.5, borderRadius: 4,
        bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: 2,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontFamily: SERIF, fontSize: 14, fontWeight: 600, color: SAGE[800] }}>
            Find me companies like…
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400] }}>
            Pick up to 4 seeds · {filledSeeds.length}/4
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500], mb: 2 }}>
          Use companies you've worked at, saved, or any name you can think of. We'll find others with similar shape.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          {seeds.map((seed, i) => (
            <SeedSlot key={i} seed={seed} allSeeds={allSeeds} onPick={onPick} onClear={onClear} slotIndex={i} />
          ))}
        </Box>
        {filledSeeds.length >= 1 && (
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5} flexWrap="wrap" sx={{
            mt: 2, pt: 2, borderTop: 1, borderColor: SAGE[200],
          }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography sx={{ fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[400], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Looking for
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                {[...new Set(stages)].map((s) => (
                  <Chip key={s} label={s} size="small" sx={{ height: 20, fontFamily: F, fontWeight: 500, fontSize: 10, bgcolor: SAGE[100], color: SAGE[700] }} />
                ))}
                <Chip label={`~${avgSize.toLocaleString()} ppl`} size="small" sx={{ height: 20, fontFamily: F, fontWeight: 500, fontSize: 10, bgcolor: SAGE[100], color: SAGE[700] }} />
                {[...new Set(modes)].map((m) => (
                  <Chip key={m} label={m} size="small" sx={{ height: 20, fontFamily: F, fontWeight: 500, fontSize: 10, bgcolor: SAGE[100], color: SAGE[700] }} />
                ))}
              </Stack>
            </Stack>
            {isScattered && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: WARM.accent }}>
                <WarningAmberIcon sx={{ fontSize: 12 }} />
                <Typography sx={{ fontFamily: F, fontSize: 10, color: WARM.accent }}>
                  <Box component="span" sx={{ fontWeight: 500 }}>Seeds are scattered</Box> · results may be broad
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Paper>

      {filledSeeds.length === 0 ? (
        <Paper variant="outlined" sx={{
          p: 5, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed',
          bgcolor: CREAM[200] + '50', borderColor: SAGE[200],
        }}>
          <Typography sx={{ fontFamily: F, fontSize: 14, fontWeight: 500, color: SAGE[700], mb: 0.5 }}>
            Add a seed to start
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500], lineHeight: 1.6, maxWidth: 360, mx: 'auto' }}>
            Two seeds works well. Three or four sharpens the match. Beyond that, the signal gets noisy.
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }}>
            <Typography sx={{ fontFamily: SERIF, fontSize: 14, fontWeight: 600, color: SAGE[800] }}>
              {results.length} {results.length === 1 ? 'match' : 'matches'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <SamplePill label="Client-side similarity (v1)" />
              <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400] }}>
                Sorted by similarity
              </Typography>
            </Stack>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
            {results.slice(0, 12).map(({ company: c, score, reasons }) => (
              <FindLikeResultCard
                key={c.id}
                company={c}
                state={getState(c.id)}
                onCycle={cycle}
                onClick={() => onCompanyClick(c)}
                score={score}
                reasons={reasons}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ===========================================================================
// MODE: BROWSE — filter the universe by attributes
// ===========================================================================

const STAGE_OPTIONS = ['Series A', 'Series B', 'Series C', 'Series D', 'Late-stage', 'Public'];
const MODE_OPTIONS  = ['Hybrid', 'Remote', 'Remote-friendly'];
const SIZE_OPTIONS = [
  { id: '<100',     label: '<100',     test: (n) => n < 100 },
  { id: '100–500',  label: '100–500',  test: (n) => n >= 100 && n < 500 },
  { id: '500–2k',   label: '500–2k',   test: (n) => n >= 500 && n < 2000 },
  { id: '2k+',      label: '2k+',      test: (n) => n >= 2000 },
];

// Multi-select chip group. Empty `selected` = no filter (matches all).
function FilterGroup({ label, options, selected, onChange }) {
  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val]);

  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
      <Typography sx={{
        fontFamily: F, fontSize: 10, fontWeight: 600, color: SAGE[400],
        textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.5,
      }}>
        {label}
      </Typography>
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.id;
        const display = typeof opt === 'string' ? opt : opt.label;
        const active = selected.includes(value);
        return (
          <Chip
            key={value}
            label={display}
            size="small"
            onClick={() => toggle(value)}
            sx={{
              height: 24, fontFamily: F, fontWeight: active ? 600 : 500, fontSize: 11,
              cursor: 'pointer',
              bgcolor: active ? SAGE[600] : SAGE[100],
              color: active ? CREAM[50] : SAGE[700],
              '&:hover': { bgcolor: active ? SAGE[700] : SAGE[200] },
            }}
          />
        );
      })}
      {selected.length > 0 && (
        <Box
          component="button"
          onClick={() => onChange([])}
          sx={{
            fontFamily: F, fontSize: 10, fontWeight: 500, color: SAGE[500],
            background: 'transparent', border: 'none', cursor: 'pointer',
            textDecoration: 'underline', ml: 0.5,
          }}
        >
          Clear
        </Box>
      )}
    </Stack>
  );
}

function ModeBrowse({ onCompanyClick }) {
  const { getState, cycle, setState } = useSelections('companies');
  const [stages, setStages] = useState([]);
  const [modes,  setModes]  = useState([]);
  const [sizes,  setSizes]  = useState([]);

  const handleDisqualify = (id) => setState(id, 'disqualified');

  const filtered = useMemo(() => {
    const sizeBucket = (n) => SIZE_OPTIONS.find((b) => b.test(n))?.id;
    return COMPANIES
      .filter((c) => getState(c.id) !== 'disqualified')
      .filter((c) => stages.length === 0 || stages.includes(c.stage))
      .filter((c) => modes.length === 0 || modes.includes(c.mode))
      .filter((c) => sizes.length === 0 || sizes.includes(sizeBucket(c.size)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [stages, modes, sizes, getState]);

  const totalActive = stages.length + modes.length + sizes.length;

  return (
    <Box>
      <Paper variant="outlined" sx={{
        p: 2.5, mb: 2.5, borderRadius: 4,
        bgcolor: CREAM[50], borderColor: SAGE[200], borderWidth: 2,
      }}>
        <Stack spacing={1.5}>
          <FilterGroup label="Stage" options={STAGE_OPTIONS} selected={stages} onChange={setStages} />
          <FilterGroup label="Mode"  options={MODE_OPTIONS}  selected={modes}  onChange={setModes} />
          <FilterGroup label="Size"  options={SIZE_OPTIONS}  selected={sizes}  onChange={setSizes} />
        </Stack>
      </Paper>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }}>
        <Typography sx={{ fontFamily: SERIF, fontSize: 14, fontWeight: 600, color: SAGE[800] }}>
          {filtered.length} {filtered.length === 1 ? 'company' : 'companies'}
        </Typography>
        <Typography sx={{ fontFamily: F, fontSize: 10, color: SAGE[400] }}>
          {totalActive === 0 ? 'No filters · alphabetical' : `${totalActive} filter${totalActive === 1 ? '' : 's'} active`}
        </Typography>
      </Stack>

      {filtered.length === 0 ? (
        <Paper variant="outlined" sx={{
          p: 5, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed',
          bgcolor: CREAM[200] + '50', borderColor: SAGE[200],
        }}>
          <Typography sx={{ fontFamily: F, fontSize: 14, fontWeight: 500, color: SAGE[700], mb: 0.5 }}>
            Nothing matches those filters
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: 11, color: SAGE[500], lineHeight: 1.6 }}>
            Try clearing one to broaden the search.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5 }}>
          {filtered.map((c) => (
            <CompanyCard
              key={c.id}
              company={c}
              state={getState(c.id)}
              onCycle={cycle}
              onDisqualify={handleDisqualify}
              onClick={() => onCompanyClick(c)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ===========================================================================
// MODE SWITCHER
// ===========================================================================

const MODES = [
  { id: 'headlines', label: 'Headlines',         Icon: MenuBookIcon, desc: 'Editorial picks' },
  { id: 'findlike',  label: 'Similar Companies', Icon: ExploreIcon,  desc: 'Similar to seeds' },
  { id: 'browse',    label: 'Browse',            Icon: TuneIcon,     desc: 'Filter by attributes' },
  { id: 'search',    label: 'Search',            Icon: SearchIcon,   desc: 'By company name' },
];

function ModeSwitcher({ mode, setMode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25, p: 0.5,
        borderRadius: 2.5, bgcolor: SAGE[100],
      }}>
        {MODES.map((m) => {
          const active = mode === m.id;
          const { Icon } = m;
          return (
            <Box
              key={m.id}
              component="button"
              onClick={() => setMode(m.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 1, borderRadius: 1.5, cursor: 'pointer',
                background: active ? CREAM[50] : 'transparent',
                color: active ? SAGE[800] : SAGE[500],
                boxShadow: active ? `0 1px 3px ${SAGE[300]}80` : 'none',
                border: 'none', fontFamily: F,
                transition: 'all 0.15s',
              }}
            >
              <Icon sx={{ fontSize: 16, color: active ? SAGE[700] : SAGE[400] }} />
              <Box sx={{ textAlign: 'left' }}>
                <Box sx={{ fontFamily: F, fontSize: 12, fontWeight: 600, lineHeight: 1 }}>{m.label}</Box>
                <Box sx={{ fontFamily: F, fontSize: 9, mt: 0.25, lineHeight: 1, color: active ? SAGE[500] : SAGE[400] }}>
                  {m.desc}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ===========================================================================
// PAGE (inner — assumes SelectionsProvider above)
// ===========================================================================

function CompaniesDiscoveryInner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cycle, setState, listType } = useSelections('companies');
  const [previewCompany, setPreviewCompany] = useState(null);

  // Mode persists in the URL (?mode=browse) so it's bookmarkable, shareable,
  // and survives reload. Default 'headlines' is omitted from the URL for
  // cleanliness — matches the ?tab= pattern in CompanyInsightsPage.
  const modeFromUrl = searchParams.get('mode');
  const mode = MODES.find((m) => m.id === modeFromUrl)?.id || 'headlines';
  const setMode = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'headlines') next.delete('mode');
    else next.set('mode', id);
    setSearchParams(next, { replace: true });
  };

  const handleDisqualify = (id) => setState(id, 'disqualified');

  const onCompare = () => {
    // The Comparison page is deferred until enrichment lands so cohort-skew
    // warnings ride on real data. Once it ships, replace with:
    //   navigate(`/explore/companies/compare?ids=${ids.join(',')}`)
    const ids = listType().filter(([, s]) => s === 'considering').map(([id]) => id);
    window.alert(`Comparison page lands next session.\n\nWould compare:\n  ${ids.join('\n  ')}`);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: `linear-gradient(170deg, ${CREAM[200]} 0%, ${CREAM[100]} 40%, ${SAGE[100]} 100%)`,
      fontFamily: F,
      m: -3,  // cancel App.js's <Box main p:3 />
      p: 3,
    }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Alert severity="info" sx={{ mb: 3, fontFamily: F }}>
          <strong>Preview:</strong> next-iteration Companies Discovery page. The live grid is at{' '}
          <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/explore/companies')}>
            /explore/companies
          </Box>
          . Selection state (eye / star / disqualified) persists via /api/selections. Mode switching, similarity scoring, and headline clusters use mock data per the brief — real wiring lands when enrichment ships.
        </Alert>

        {/* Page header */}
        <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: SERIF, fontSize: 36, fontWeight: 600, color: SAGE[800], mb: 0.25 }}>
              Companies
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: 14, color: SAGE[400] }}>
              {COMPANIES.length} companies · curated to match your shape
            </Typography>
          </Box>
        </Stack>

        <ModeSwitcher mode={mode} setMode={setMode} />

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 320px' } }}>
          <Box>
            {mode === 'search'    && <ModeSearch    onCompanyClick={setPreviewCompany} />}
            {mode === 'browse'    && <ModeBrowse    onCompanyClick={setPreviewCompany} />}
            {mode === 'headlines' && <ModeHeadlines onCompanyClick={setPreviewCompany} />}
            {mode === 'findlike'  && <ModeFindLike  onCompanyClick={setPreviewCompany} />}
          </Box>
          <Box>
            <CompaniesRail onClick={setPreviewCompany} onCompare={onCompare} />
          </Box>
        </Box>
      </Box>

      <CompanyDetailModal
        company={previewCompany}
        onClose={() => setPreviewCompany(null)}
        onCycle={cycle}
        onDisqualify={handleDisqualify}
      />
    </Box>
  );
}

export default function CompaniesDiscoveryPage() {
  return (
    <SelectionsProvider>
      <CompaniesDiscoveryInner />
    </SelectionsProvider>
  );
}
